import React, { useMemo, useState } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar, Filter, ChevronDown, BarChart } from "lucide-react";

interface DataPoint {
  _count: {
    id: number;
  };
  createdAt: string;
}

interface StatsChartProps {
  data: DataPoint[];
  loading?: boolean;
}

const StatsChart: React.FC<StatsChartProps> = ({ data, loading }) => {
  const [filter, setFilter] = useState<"Day" | "Month" | "Year">("Day");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const processedData = useMemo(() => {
    if (!data || data?.length === 0) return [];

    const grouped: Record<string, number> = {};

    data.forEach((item) => {
      const date = new Date(item.createdAt);
      let key = "";

      if (filter === "Day") {
        key = date.toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
        });
      } else if (filter === "Month") {
        key = date.toLocaleDateString("en-IN", {
          month: "long",
          year: "2-digit",
        });
      } else {
        key = date.getFullYear()?.toString();
      }

      grouped[key] = (grouped[key] || 0) + item._count.id;
    });

    // Convert back to array and sort (very simple sorting by date key)
    return Object.entries(grouped)
      .map(([name, orders]) => ({ name, orders }))
      .slice(-12); // Show last 12 points
  }, [data, filter]);

  if (loading) {
    return (
      <div className="h-[300px] w-full bg-slate-50/50 rounded-2xl animate-pulse flex items-center justify-center">
        <div className="text-slate-400 font-medium">
          Loading sales analytics...
        </div>
      </div>
    );
  }

  return (
    <div className="inner-card p-6 h-full flex flex-col">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-sm font-bold text-slate-900 uppercase tracking-widest flex items-center gap-2">
            <Calendar size={16} className="text-primary" />
            Order Analytics
          </h2>
          <p className="text-[10px] text-slate-400 mt-1">
            Real-time order volume trends
          </p>
        </div>

        <div className="relative">
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="flex items-center gap-2 px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-600 hover:border-primary transition-all"
          >
            <Filter size={12} />
            {filter} wise
            <ChevronDown
              size={12}
              className={`transition-transform ${isDropdownOpen ? "rotate-180" : ""}`}
            />
          </button>

          <AnimatePresence>
            {isDropdownOpen && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                className="absolute top-full right-0 mt-1 w-32 bg-white border border-slate-100 rounded-xl shadow-xl z-20 overflow-hidden p-1"
              >
                {(["Day", "Month", "Year"] as const).map((opt) => (
                  <button
                    key={opt}
                    onClick={() => {
                      setFilter(opt);
                      setIsDropdownOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                      filter === opt
                        ? "bg-primary/10 text-primary"
                        : "text-slate-500 hover:bg-slate-50"
                    }`}
                  >
                    {opt} view
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <div className="flex-1 w-full min-h-[250px]">
        {processedData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={processedData}>
              <defs>
                <linearGradient id="colorOrders" x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="5%"
                    stopColor="var(--primary)"
                    stopOpacity={0.3}
                  />
                  <stop
                    offset="95%"
                    stopColor="var(--primary)"
                    stopOpacity={0}
                  />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke="#f1f5f9"
              />
              <XAxis
                dataKey="name"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: "#94a3b8", fontWeight: 600 }}
                dy={10}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: "#94a3b8", fontWeight: 600 }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#fff",
                  borderRadius: "12px",
                  border: "none",
                  boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1)",
                  fontSize: "12px",
                  fontWeight: "700",
                }}
              />
              <Area
                type="monotone"
                dataKey="orders"
                stroke="var(--primary)"
                strokeWidth={3}
                fillOpacity={1}
                fill="url(#colorOrders)"
                animationDuration={1500}
                animationEasing="ease-in-out"
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex flex-col items-center justify-center text-slate-300 gap-2">
            <BarChart size={48} strokeWidth={1} />
            <p className="text-sm font-medium">Insufficient data for graph</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default StatsChart;
